/** Cloud API + token session. Installed once so existing fetch/axios/WebSocket calls stay token-based. */
import { createSocketMessageNormalizer } from "./normalizeLivePayload";
import { isLiveStreamUrl, trackLiveSocket } from "./liveConnection";

const LOCAL_HOST = "localhost";
const LEGACY_ALB_HOST = "haechie-alb-prod-848504357.ap-south-1.elb.amazonaws.com";
/** Set per build: `.env.production` (platform.ehaechie.com) or `.env.testing` (core.ehaechie.com). */
const PROD_API_HOST = process.env.REACT_APP_API_HOST || "platform.ehaechie.com";
/** Any of these in a backend URL is rewritten to the host of the current build. */
const KNOWN_CLOUD_HOSTS = new Set(["platform.ehaechie.com", "core.ehaechie.com", LEGACY_ALB_HOST]);

/** CRA sets NODE_ENV=production for `npm run build` / `npm run build:testing` (S3). `npm start` uses localhost. */
const isProductionBuild = process.env.NODE_ENV === "production";

export const CLOUD_API_HOST = isProductionBuild ? PROD_API_HOST : LOCAL_HOST;
export const CLOUD_API_BASE = isProductionBuild
  ? `https://${PROD_API_HOST}`
  : `http://${LOCAL_HOST}:8000`;
export const CLOUD_WS_BASE = isProductionBuild
  ? `wss://${PROD_API_HOST}`
  : `ws://${LOCAL_HOST}:8050`;

const SESSION_KEY = "haechieAuth";
const LEGACY_AUTH_KEY = "isAuthenticated";
const LEGACY_USER_KEY = "username";

const BACKEND_PORTS = new Set(["8000", "8050", "8060"]);

let nativeFetch = null;
let refreshInFlight = null;
let renewalTimer = 0;

const readSession = () => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeSession = (session) => {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  localStorage.setItem(LEGACY_AUTH_KEY, "true");
  if (session?.username) localStorage.setItem(LEGACY_USER_KEY, session.username);
  scheduleSessionRenewal();
};

export const getAccessToken = () => readSession()?.token || "";
export const getRefreshToken = () => readSession()?.refresh || "";

export const getAuthSession = () => readSession();

export const getAuthDisplayName = () => {
  const session = readSession();
  return (
    session?.username ||
    session?.tenant?.companies?.[0]?.name ||
    localStorage.getItem(LEGACY_USER_KEY) ||
    ""
  );
};

const decodeJwtPayload = (token) => {
  try {
    const part = String(token).split(".")[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const tokenExpiresAt = (token) => {
  const exp = decodeJwtPayload(token)?.exp;
  return exp ? exp * 1000 : 0;
};

export const isAccessTokenValid = (token = getAccessToken()) => {
  if (!token) return false;
  const expiresAt = tokenExpiresAt(token);
  if (!expiresAt) return true;
  return expiresAt > Date.now() + 5000;
};

export const isAuthenticated = () => isAccessTokenValid();

export const clearAuthSession = () => {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(LEGACY_AUTH_KEY);
  localStorage.removeItem(LEGACY_USER_KEY);
  window.clearTimeout(renewalTimer);
  renewalTimer = 0;
};

export const saveAuthSession = (payload, username) => {
  const token = payload?.token || payload?.access;
  if (!token) throw new Error("Login response did not include a token");
  writeSession({
    token,
    refresh: payload?.refresh || "",
    username: username || payload?.username || "",
    next_step: payload?.next_step || "dashboard",
    tenant: payload?.tenant || null,
    savedAt: Date.now(),
  });
  return readSession();
};

const errorMessageFromBody = (data, fallback) => {
  const raw = data?.detail || data?.error || data?.message || data?.non_field_errors;
  if (Array.isArray(raw)) return raw.join(" ");
  if (typeof raw === "string" && raw.trim()) return raw;
  return fallback;
};

export const loginWithPassword = async (username, password) => {
  const res = await fetch(`${CLOUD_API_BASE}/auth/login/`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ username, password, client: "web" }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !(data.token || data.access)) {
    throw new Error(errorMessageFromBody(data, "Invalid username or password"));
  }
  return saveAuthSession(data, username);
};

const isPrivateOrLocalHost = (hostname) => {
  if (!hostname) return false;
  if (hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]") return true;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) return true;
  return false;
};

const isBackendApiUrl = (url) => {
  try {
    const parsed = new URL(String(url), window.location.href);
    if (isProductionBuild) return parsed.hostname === PROD_API_HOST;
    return (
      (parsed.hostname === LOCAL_HOST || parsed.hostname === "127.0.0.1") &&
      BACKEND_PORTS.has(parsed.port)
    );
  } catch {
    return false;
  }
};

export const rewriteTransportUrl = (url) => {
  if (!url || typeof url !== "string") return url;
  if (url.startsWith("blob:") || url.startsWith("data:") || url.startsWith("./") || url.startsWith("../")) {
    return url;
  }
  let parsed;
  try {
    parsed = new URL(url, window.location.href);
  } catch {
    return url;
  }
  const isWs = parsed.protocol === "ws:" || parsed.protocol === "wss:";
  const isHttp = parsed.protocol === "http:" || parsed.protocol === "https:";
  if (!isWs && !isHttp) return url;

  if (!isProductionBuild) {
    const targetsBackend =
      BACKEND_PORTS.has(parsed.port) ||
      parsed.hostname === PROD_API_HOST ||
      KNOWN_CLOUD_HOSTS.has(parsed.hostname);
    if (!targetsBackend) return url;
    const protocol = isWs ? "ws:" : "http:";
    const port = isWs ? "8050" : "8000";
    return `${protocol}//${LOCAL_HOST}:${port}${parsed.pathname}${parsed.search}${parsed.hash}`;
  }

  const alreadyCloud = parsed.hostname === CLOUD_API_HOST && !BACKEND_PORTS.has(parsed.port);
  if (alreadyCloud) {
    if (parsed.protocol === "http:" || parsed.protocol === "ws:") {
      const base = isWs ? CLOUD_WS_BASE : CLOUD_API_BASE;
      return `${base}${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
    return url;
  }
  const shouldRewrite =
    BACKEND_PORTS.has(parsed.port) ||
    isPrivateOrLocalHost(parsed.hostname) ||
    parsed.hostname === CLOUD_API_HOST ||
    KNOWN_CLOUD_HOSTS.has(parsed.hostname);
  if (!shouldRewrite) return url;
  const base = isWs ? CLOUD_WS_BASE : CLOUD_API_BASE;
  return `${base}${parsed.pathname}${parsed.search}${parsed.hash}`;
};

export const withMediaAuth = (url) => {
  if (!url) return url;
  const raw = String(url);
  const absolute = raw.startsWith("/") && !raw.startsWith("//") ? `${CLOUD_API_BASE}${raw}` : raw;
  const next = rewriteTransportUrl(absolute);
  const token = getAccessToken();
  if (!next || !token || !isBackendApiUrl(next)) return next;
  if (/[?&]token=/.test(next)) return next;
  return `${next}${next.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}`;
};

const isLoginUrl = (url) => String(url).includes("/auth/login/");
const isRefreshUrl = (url) => String(url).includes("/auth/refresh/");

const appendAuthHeader = (headers, url) => {
  const next = new Headers(headers || undefined);
  const token = getAccessToken();
  if (token && isBackendApiUrl(url) && !isLoginUrl(url) && !next.has("Authorization")) {
    next.set("Authorization", `Bearer ${token}`);
  }
  return next;
};

const redirectToLogin = () => {
  clearAuthSession();
  const path = window.location.pathname || "/";
  if (path === "/" || path === "") return;
  window.location.assign("/");
};

/**
 * Exchanges the refresh token for a new access token.
 * Resolves "ok", "rejected" (session is over) or "network" (server unreachable; session may still be fine).
 */
const refreshSession = async () => {
  const refresh = getRefreshToken();
  if (!refresh) return "rejected";
  if (!nativeFetch) return "network";
  if (!refreshInFlight) {
    const tokenBefore = getAccessToken();
    refreshInFlight = (async () => {
      try {
        const res = await nativeFetch(`${CLOUD_API_BASE}/auth/refresh/`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ refresh }),
        });
        const data = await res.json().catch(() => ({}));
        const token = data.token || data.access;
        if (res.ok && token) {
          const current = readSession() || {};
          writeSession({ ...current, token, refresh: data.refresh || current.refresh || refresh });
          return "ok";
        }
        if (res.status >= 500) return "network";
        /* Another tab may have renewed (and rotated the refresh token) first. */
        if (getAccessToken() !== tokenBefore && isAccessTokenValid()) return "ok";
        return "rejected";
      } catch {
        return "network";
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
};

const refreshAccessToken = async () => (await refreshSession()) === "ok";

/** Used by the route guard: true when the session is (or could be made) valid. */
export const renewAccessToken = refreshAccessToken;

let lastBackgroundRenewAt = 0;

/** Background refresh for sockets / XHR: ends the session only when the server rejects it. */
const renewOrLogout = () => {
  const now = Date.now();
  if (!refreshInFlight && now - lastBackgroundRenewAt < 10_000) return;
  lastBackgroundRenewAt = now;
  refreshSession().then((result) => {
    if (result === "rejected" && !isAccessTokenValid()) redirectToLogin();
  });
};

const RENEW_AHEAD_MS = 60_000;
const RENEW_RETRY_MS = 30_000;
const MAX_TIMER_MS = 2_147_000_000;

const runSessionRenewal = async () => {
  renewalTimer = 0;
  const token = getAccessToken();
  if (!token) return;
  const expiresAt = tokenExpiresAt(token);
  if (!expiresAt) return;
  if (expiresAt - Date.now() > RENEW_AHEAD_MS + 15_000) {
    scheduleSessionRenewal();
    return;
  }
  const result = await refreshSession();
  if (result === "ok") return;
  if (result === "network") {
    renewalTimer = window.setTimeout(runSessionRenewal, RENEW_RETRY_MS);
    return;
  }
  if (!isAccessTokenValid()) {
    redirectToLogin();
    return;
  }
  /* Refresh refused but the access token still works: log out the moment it expires. */
  const untilExpiry = tokenExpiresAt(getAccessToken()) - 5000 - Date.now();
  renewalTimer = window.setTimeout(() => {
    renewalTimer = 0;
    if (!isAccessTokenValid()) redirectToLogin();
    else scheduleSessionRenewal();
  }, Math.max(1000, untilExpiry));
};

/** Renews the access token shortly before it expires, so the user is never cut off mid-session. */
function scheduleSessionRenewal() {
  if (typeof window === "undefined") return;
  window.clearTimeout(renewalTimer);
  renewalTimer = 0;
  const token = getAccessToken();
  if (!token) return;
  const expiresAt = tokenExpiresAt(token);
  if (!expiresAt) return;
  /* Jitter so several open tabs do not all refresh at the same instant. */
  const jitter = Math.random() * 10_000;
  const delay = Math.max(1000, expiresAt - RENEW_AHEAD_MS - jitter - Date.now());
  renewalTimer = window.setTimeout(runSessionRenewal, Math.min(delay, MAX_TIMER_MS));
}

const installSessionRenewal = () => {
  if (window.__haechieSessionRenewal) return;
  window.__haechieSessionRenewal = true;
  /* Timers pause while the PC sleeps; re-check on wake / network return / other-tab changes. */
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") scheduleSessionRenewal();
  });
  window.addEventListener("online", scheduleSessionRenewal);
  window.addEventListener("storage", (event) => {
    if (event.key === SESSION_KEY) scheduleSessionRenewal();
  });
  scheduleSessionRenewal();
};

const installFetchAuth = () => {
  if (nativeFetch) return;
  nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const rawUrl = typeof input === "string" ? input : input?.url;
    const url = rewriteTransportUrl(rawUrl);
    const headers = appendAuthHeader(init.headers || (typeof input !== "string" ? input.headers : undefined), url);
    const nextInit = { ...init, headers };
    const nextInput = typeof input === "string" ? url : new Request(url, input);
    const response = await nativeFetch(nextInput, nextInit);
    if (response.status !== 401 || isLoginUrl(url) || isRefreshUrl(url)) return response;
    const result = await refreshSession();
    if (result !== "ok") {
      if (result === "rejected") redirectToLogin();
      return response;
    }
    const retryHeaders = appendAuthHeader(init.headers, url);
    return nativeFetch(typeof input === "string" ? url : new Request(url, input), { ...init, headers: retryHeaders });
  };
};

const installXhrAuth = () => {
  const proto = window.XMLHttpRequest?.prototype;
  if (!proto || proto.__haechieAuthInstalled) return;
  const origOpen = proto.open;
  const origSend = proto.send;
  const origSetHeader = proto.setRequestHeader;
  proto.open = function open(method, url, ...rest) {
    this.__haechieHeaders = {};
    this.__haechieUrl = rewriteTransportUrl(String(url));
    return origOpen.call(this, method, this.__haechieUrl, ...rest);
  };
  proto.setRequestHeader = function setRequestHeader(name, value) {
    this.__haechieHeaders = this.__haechieHeaders || {};
    this.__haechieHeaders[String(name).toLowerCase()] = value;
    return origSetHeader.call(this, name, value);
  };
  proto.send = function send(body) {
    const url = this.__haechieUrl || "";
    const token = getAccessToken();
    const headers = this.__haechieHeaders || {};
    const isAuthedCall = isBackendApiUrl(url) && !isLoginUrl(url) && !isRefreshUrl(url);
    if (token && isAuthedCall && !headers.authorization) {
      try {
        origSetHeader.call(this, "Authorization", `Bearer ${token}`);
      } catch {
        /* request already sending */
      }
    }
    if (isAuthedCall) {
      this.addEventListener(
        "loadend",
        () => {
          if (this.status === 401) renewOrLogout();
        },
        { once: true }
      );
    }
    return origSend.call(this, body);
  };
  proto.__haechieAuthInstalled = true;
};

const installWebSocketAuth = () => {
  if (window.WebSocket?.__haechieAuthInstalled) return;
  const NativeWS = window.WebSocket;
  function AuthedWebSocket(url, protocols) {
    let next = rewriteTransportUrl(String(url));
    const token = getAccessToken();
    /* The page retries shortly after this attempt fails; by then the renewed token is in place. */
    if (token && !isAccessTokenValid(token)) renewOrLogout();
    if (token && (next.startsWith("ws://") || next.startsWith("wss://")) && !/[?&]token=/.test(next)) {
      next = `${next}${next.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}`;
    }
    const socket = protocols !== undefined ? new NativeWS(next, protocols) : new NativeWS(next);
    const normalizeSocketMessage = createSocketMessageNormalizer();
    if (isLiveStreamUrl(next)) {
      const nativeClose = socket.close.bind(socket);
      const live = trackLiveSocket(socket, {
        listen: socket.addEventListener.bind(socket),
        close: nativeClose,
      });
      socket.close = (...args) => {
        live.markIntentionalClose();
        return nativeClose(...args);
      };
    }
    const messageDesc = Object.getOwnPropertyDescriptor(NativeWS.prototype, "onmessage");
    if (messageDesc?.set) {
      let userHandler = null;
      Object.defineProperty(socket, "onmessage", {
        configurable: true,
        enumerable: true,
        get() {
          return userHandler;
        },
        set(fn) {
          userHandler = typeof fn === "function" ? fn : null;
          messageDesc.set.call(
            socket,
            userHandler
              ? (event) => userHandler.call(socket, normalizeSocketMessage(event))
              : null
          );
        },
      });
    }
    const nativeListen = socket.addEventListener.bind(socket);
    socket.addEventListener = (type, listener, options) => {
      if (type !== "message" || typeof listener !== "function") {
        return nativeListen(type, listener, options);
      }
      return nativeListen(type, (event) => listener(normalizeSocketMessage(event)), options);
    };
    return socket;
  }
  AuthedWebSocket.prototype = NativeWS.prototype;
  AuthedWebSocket.CONNECTING = NativeWS.CONNECTING;
  AuthedWebSocket.OPEN = NativeWS.OPEN;
  AuthedWebSocket.CLOSING = NativeWS.CLOSING;
  AuthedWebSocket.CLOSED = NativeWS.CLOSED;
  AuthedWebSocket.__haechieAuthInstalled = true;
  window.WebSocket = AuthedWebSocket;
};

export const installCloudAuth = () => {
  if (typeof window === "undefined") return;
  installFetchAuth();
  installXhrAuth();
  installWebSocketAuth();
  installSessionRenewal();
};

export const logout = () => {
  clearAuthSession();
};
